import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const TOKEN_KEY = "apex_token";

const AuthContext = createContext(null);

const setAuthHeader = (token) => {
  if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  else delete axios.defaults.headers.common["Authorization"];
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(!!token);

  useEffect(() => {
    setAuthHeader(token);
    if (!token) {
      setAccount(null);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/auth/me`)
      .then((r) => alive && setAccount(r.data))
      .catch(() => {
        if (alive) {
          localStorage.removeItem(TOKEN_KEY);
          setAuthHeader(null);
          setToken(null);
          setAccount(null);
        }
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [token]);

  const persist = (tk, acc) => {
    localStorage.setItem(TOKEN_KEY, tk);
    setAuthHeader(tk);
    setToken(tk);
    setAccount(acc);
  };

  const login = async (email, password) => {
    const r = await axios.post(`${API}/auth/login`, { email, password });
    persist(r.data.token, r.data.account);
  };

  const register = async (payload) => {
    const r = await axios.post(`${API}/auth/register`, payload);
    persist(r.data.token, r.data.account);
  };

  // Inicia sesión con lo que devuelve el servidor (tras restablecer la clave).
  const adoptSession = ({ token: tk, account: acc }) => persist(tk, acc);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthHeader(null);
    setToken(null);
    setAccount(null);
  };

  const refresh = async () => {
    const r = await axios.get(`${API}/auth/me`);
    setAccount(r.data);
  };

  const purchaseMembership = async (packageId) => {
    const r = await axios.post(`${API}/memberships/purchase`, { package_id: packageId });
    // Con Stripe activo el paquete se paga primero: se envía al checkout seguro.
    if (r.data && r.data.requires_payment && r.data.checkout_url) {
      window.location.href = r.data.checkout_url;
      return { redirected: true };
    }
    setAccount(r.data);
    return { redirected: false };
  };

  const updateProfile = async (data) => {
    const r = await axios.patch(`${API}/auth/me`, data);
    setAccount(r.data);
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        account,
        loading,
        isAuth: !!token && !!account,
        login,
        register,
        logout,
        refresh,
        purchaseMembership,
        adoptSession,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
